import { Controller } from "@hotwired/stimulus"

// Card #3325 -- the show page's "Prior cycle" toggle button
// (decks/_slide_toolbar.html.erb's show_prior_toggle) and the hidden
// prior-slide comparison content it swaps in
// (decks/_slide_frame.html.erb's eagerly-rendered prior_resolved sibling)
// both stop rendering entirely after ANY Turbo Stream broadcast for the
// slide -- an MCP update_deck_slide call, or a live browser edit like a
// table cell edit.
//
// SAME BUG CLASS as card #2834 (the right-click context menu, see
// deck_element_context_menu_controller.js's extensive header comment for
// the full precedent) but NOT the same fix shape. #2834 re-attached missing
// DATA ATTRIBUTES on DOM nodes that still existed after the broadcast. Here
// the DOM nodes themselves vanish entirely: both show_prior_toggle and
// prior_resolved are gated on `internal_viewer`, which every broadcast
// renders as `false` (deliberately -- see those two locals' own comments in
// _slide_toolbar.html.erb / _slide_frame.html.erb: a server push has no
// per-tab request context, and `turbo_stream_from "deck_#{id}"` is the SAME
// channel a client-facing viewer on a shared link subscribes to, so a
// broadcast can never risk rendering internal-only content). There is
// nothing to reattach an attribute TO once the node itself is gone --
// this controller instead CACHES the button and prior-content markup once,
// at connect (this tab's own initial page render, the only render that can
// ever have `internal_viewer` true), and re-inserts the cached node back
// into its known DOM location whenever a broadcast strips it.
//
// GATED BY THIS CONTROLLER'S OWN PRESENCE, same mechanism as
// deck-element-context-menu: decks/_viewer.html.erb only attaches this
// controller to the outer `.airdrop-viewer` wrapper when `internal_viewer`
// was true at THIS TAB'S OWN initial render (decks/show.html.erb is the
// only caller that ever passes it). If this controller is connected at
// all, this tab is internal-viewer, full stop -- no new permission check
// needed, and a tab that was NOT internal-viewer at initial load never
// gets this controller attached, so it can never gain the toggle from a
// broadcast (no leak to a client-facing viewer on the same "deck_#{id}"
// channel).
//
// WHY CACHE ONCE AT CONNECT, NOT RE-CAPTURE ON EVERY REATTACH PASS: a
// slide's eligibility for the toggle (Deck#prior_cycle_slide resolving) is
// fixed for the lifetime of this page render -- a slide that had no prior
// cycle equivalent at page load will not suddenly gain one mid-session, and
// a broadcast can only ever REMOVE these nodes (internal_viewer always
// renders false), never legitimately add a new one. Capturing the clean,
// freshly-loaded markup once avoids ever caching a "dirty" snapshot that
// might carry mid-toggle classes/aria-state from a user interaction, and
// keeps every restore idempotent and predictable: always the same known-
// good default markup going back in.
//
// TOGGLE STATE ACROSS A BROADCAST (deliberately NOT preserved, nice-to-have
// per the card, not required): even if the cached button/content nodes were
// reinserted mid-"showing prior" state, the CURRENT slide's own turbo frame
// (decks/_slide_frame.html.erb) always re-renders with
// `style="display: block"` baked into every broadcast (that's the frame's
// own unconditional markup, independent of this fix) -- so the current
// slide would reappear regardless, leaving a stale "on" toggle pointing at
// content that's no longer showing. Restoring both nodes to their clean
// default (current showing, prior hidden, toggle unpressed) matches
// _slide_frame.html.erb's own documented broadcast behaviour ("never
// resolved on a broadcast re-render ... conservatively re-renders showing
// the current slide only, same as every other broadcast path").
//
// DOM ANCHORS (both deterministic, primitive-built strings shared with the
// server -- Deck.slide_toolbar_id / Deck.slide_frame_id / Deck.slide_prior_id,
// see deck.rb):
//   - the button is reinserted into `#deck_<deckId>_slide_toolbar_<uuid>`
//     (the toolbar's OWN id survives every broadcast -- only its inner
//     content gets replaced), immediately before that toolbar's Copy Slide
//     button (the one item in that same right-hand cluster that is NEVER
//     conditionally gated, so it's a stable insertion anchor).
//   - the prior-content div is reinserted as the next sibling of
//     `#deck_<deckId>_slide_<uuid>` (the slide's own turbo frame, also a
//     stable id that survives every broadcast) -- the exact position it
//     occupies on a normal page render (decks/_slide_frame.html.erb).
export default class extends Controller {
  static values = { deckId: Number }

  connect() {
    this.cache = new Map()
    this._captureAll()

    this.boundHandleBeforeStreamRender = (event) => this.handleBeforeStreamRender(event)
    document.addEventListener("turbo:before-stream-render", this.boundHandleBeforeStreamRender)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.boundHandleBeforeStreamRender)
  }

  // Not narrow-scoped by stream target -- same reasoning as
  // deck_element_context_menu_controller.js#handleBeforeStreamRender: both
  // a single-slide broadcast (broadcast_slide_refresh!/broadcast_slide_toolbar_refresh!)
  // and a whole-list structural broadcast (broadcast_deck_refresh!) can
  // strip these nodes, and re-scanning every cached slide is cheap and
  // idempotent (a no-op once everything is already present).
  handleBeforeStreamRender(event) {
    const defaultRender = event.detail.render
    event.detail.render = async (streamElement) => {
      await defaultRender(streamElement)
      this._reattachAll()
    }
  }

  _reattachAll() {
    this.cache.forEach((entry, slideUuid) => this._reattachSlide(slideUuid, entry))
  }

  _reattachSlide(slideUuid, entry) {
    if (entry.buttonHTML) this._reattachButton(slideUuid, entry.buttonHTML)
    if (entry.priorHTML) this._reattachPriorContent(slideUuid, entry.priorHTML)
  }

  // Scans the whole viewer ONCE (connect-time only, see header comment) for
  // every slide that currently carries a Prior cycle toggle button and/or
  // its matching hidden prior-content sibling, caching each by the slide's
  // own uuid (parsed back out of the deterministic dom id).
  _captureAll() {
    const toolbarPrefix = `deck_${this.deckIdValue}_slide_toolbar_`
    this.element.querySelectorAll(`[id^="${toolbarPrefix}"]`).forEach((toolbar) => {
      const button = toolbar.querySelector('[data-controller~="deck-slide-prior-toggle"]')
      if (!button) return

      const slideUuid = toolbar.id.slice(toolbarPrefix.length)
      const entry = this.cache.get(slideUuid) || {}
      entry.buttonHTML = button.outerHTML
      this.cache.set(slideUuid, entry)
    })

    const priorPrefix = `deck_${this.deckIdValue}_slide_prior_`
    this.element.querySelectorAll(`[id^="${priorPrefix}"]`).forEach((priorEl) => {
      const slideUuid = priorEl.id.slice(priorPrefix.length)
      const entry = this.cache.get(slideUuid) || {}
      entry.priorHTML = priorEl.outerHTML
      this.cache.set(slideUuid, entry)
    })
  }

  _reattachButton(slideUuid, buttonHTML) {
    const toolbar = document.getElementById(`deck_${this.deckIdValue}_slide_toolbar_${slideUuid}`)
    if (!toolbar) return
    if (toolbar.querySelector('[data-controller~="deck-slide-prior-toggle"]')) return

    // Copy Slide is the one item in this toolbar's right-hand cluster that
    // is never conditionally gated (decks/_slide_toolbar.html.erb) -- a
    // stable anchor to insert directly before, landing the restored button
    // in the exact same position it occupies on a normal page render.
    const copySlideButton = toolbar.querySelector('[data-action="copy-slide#copy"]')
    if (!copySlideButton || !copySlideButton.parentElement) return

    const restored = this._parseElement(buttonHTML)
    if (restored) copySlideButton.parentElement.insertBefore(restored, copySlideButton)
  }

  _reattachPriorContent(slideUuid, priorHTML) {
    const priorId = `deck_${this.deckIdValue}_slide_prior_${slideUuid}`
    if (document.getElementById(priorId)) return

    const frame = document.getElementById(`deck_${this.deckIdValue}_slide_${slideUuid}`)
    if (!frame) return

    const restored = this._parseElement(priorHTML)
    if (restored) frame.insertAdjacentElement("afterend", restored)
  }

  _parseElement(html) {
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    return template.content.firstElementChild
  }
}
